import React, { CSSProperties, FC } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useHistory } from 'react-router';
import { useButton, useI18n } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import BackIcon from '~Assets/icons/arrow_back.svg';
import bLogo from '~Assets/Logos/b_logo_black.svg';
import { BackButton } from '~ComponentsImproved/BackTopBar';
import { useEposTheme } from '../../Providers/ThemeProvider';

interface Props {
  progressText: string;
  progress: number;
  venueId: string;
  /** The venue theme as CSS variables, so the loader is already in brand colors. */
  themeVars: CSSProperties;
}

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const Wrapper = styled.div`
  height: 100vh;
  width: 100vw;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: var(--eposFontFamily);
  background: var(--eposPrimary);
`;

const TopBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  padding: 16px;
`;

/** Own text rather than BackTopBar's Title, which has a fixed color. */
const BackTitle = styled.span`
  margin-left: 8px;
  font-size: 20px;
  font-weight: 600;
  color: var(--eposAccent);
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: 240px;
  height: 22px;
  border-radius: 20px;
  color: var(--eposAccent);
  border: 2px solid;
  position: relative;
  margin-top: 40px;

  &::before {
    content: '';
    position: absolute;
    margin: 2px;
    inset: 0 ${({ progress }) => 100 - progress}% 0 0;
    border-radius: inherit;
    background: currentColor;
    transition: inset 0.5s ease-out;
  }
`;

/** Gray card behind the B logo, same as the order number box, so it is visible on any venue background. As wide as the progress bar incl. border. */
const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 244px;
  padding: 40px 0;
  border-radius: 32px;
  background: var(--colorBlueGray100);
`;

/** Fixed size, since the B logo SVG has no intrinsic size and collapses otherwise. Only the logo pulses. */
const Logo = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
`;

/** The venue's own logo sits straight on the venue background, no card. */
const VenueLogo = styled(Logo)`
  width: 240px;
  height: 120px;
`;

const ProgressText = styled.h3`
  margin: 16px 0 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--eposAccent);
`;

/** Themed loading screen for the EPOS flow, shown while the menu data downloads. */
const InitialLoader: FC<Props> = ({ progressText, progress, venueId, themeVars }) => {
  const { i18n } = useI18n();
  const history = useHistory();
  const eposTheme = useEposTheme();

  const backButton = useButton({
    onClick: () => history.push(`/venue/${venueId}`),
  });

  return (
    <Wrapper style={themeVars}>
      <TopBar>
        <BackButton {...backButton} dark>
          <Icon src={BackIcon} color="var(--eposAccent)" />
        </BackButton>
        <BackTitle>{i18n('SSK.Return')}</BackTitle>
      </TopBar>

      {eposTheme.iconImageSrcUrl ? (
        <VenueLogo src={eposTheme.iconImageSrcUrl} alt="" />
      ) : (
        <LogoWrapper>
          <Logo src={bLogo} alt="" />
        </LogoWrapper>
      )}

      <ProgressBar progress={progress} />

      <ProgressText>{progressText}</ProgressText>
    </Wrapper>
  );
};

export default InitialLoader;
